// /auth/callback — destino post email-confirm de Supabase Auth.
//
// El flow:
//   1. Usuario clickea link de confirm email → Supabase activa sesión +
//      redirige acá (configurado vía emailRedirectTo en signUp).
//   2. Esperamos a que AuthProvider resuelva la sesión + userRole.
//   3. Si userRole resolvió OK (= ya existe row en dominio): redirige
//      a su home. Caso típico: re-click del link, o callback de magic
//      link de un user existente.
//   4. Si userRole es null (= huérfano: auth.users existe pero sin row
//      en kines ni pacientes): es el caso recién-signupeado. Leemos
//      user_metadata y llamamos la RPC correspondiente para crear el
//      row de dominio.
//   5. Branches de error con CTA claro (re-login, contactar soporte,
//      token expirado para paciente, etc).
//
// Sub-decisión sobre identificación de rol: confiamos en
// user_metadata.role (seteada por SignupKine/SignupPaciente al firmar).
// Si falta (link viejo pre-B8 o magic link sin metadata), tratamos
// como huérfano sin role → signOut + mensaje genérico.

import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router";
import { Loader2, AlertCircle, Clock, CheckCircle2 } from "lucide-react";
import { supabase } from "@/lib/supabase/client";
import { useAuth } from "@/app/components/auth/AuthProvider";
import { AuthLayout } from "@/app/components/auth/AuthLayout";
import { Button } from "@/app/components/ui/button";

type CallbackState =
  | { kind: "working" }
  | { kind: "success" }
  | {
      kind: "error";
      title: string;
      message: string;
      cta: "login" | "signup-paciente-error" | "contact-support";
      ctaPayload?: { errorCode?: string };
    };

export function AuthCallback() {
  const navigate = useNavigate();
  const { status, userId, userRole, session, signOut } = useAuth();
  const [state, setState] = useState<CallbackState>({ kind: "working" });
  // Evita procesar 2 veces (StrictMode mount-unmount-mount + lookup pending).
  const processedRef = useRef(false);

  // Safety net: si el spinner queda más de 15s, asumimos que algo en la
  // cadena (lookup colgado, RPC sin responder, etc) está bloqueado y
  // mostramos error con CTA. Mejor que dejar al user en spinner eterno.
  // No reemplaza el fix de causa raíz — es defensa en profundidad.
  useEffect(() => {
    if (state.kind !== "working") return;
    const timeoutId = setTimeout(() => {
      setState((s) => {
        if (s.kind !== "working") return s;
        console.warn(
          "[AuthCallback] timeout 15s — flow no progresó. Posible userRole/RPC hang.",
        );
        return {
          kind: "error",
          title: "Tardamos demasiado",
          message:
            "Hubo un problema activando tu cuenta. Iniciá sesión o contactanos si el problema persiste.",
          cta: "login",
        };
      });
    }, 15_000);
    return () => clearTimeout(timeoutId);
  }, [state.kind]);

  useEffect(() => {
    // Espera al AuthProvider — necesitamos sesión + lookup completo.
    if (status === "loading") return;
    if (status === "anonymous") {
      // Llegó al callback sin sesión activa — link inválido / expirado
      // de email confirm, o el user salió manualmente del flow.
      setState({
        kind: "error",
        title: "Link inválido",
        message:
          "Este link de confirmación no es válido o ya fue usado. Iniciá sesión con tu email y contraseña.",
        cta: "login",
      });
      return;
    }

    // status === 'authenticated' — userId garantizado.
    if (!userId || !session) return;

    // userRole=undefined → lookup en curso. Esperá al próximo render.
    if (userRole === undefined) return;

    if (processedRef.current) return;
    processedRef.current = true;

    if (userRole === "kine" || userRole === "paciente") {
      // Ya existe row en dominio — re-click del link, o user con cuenta.
      // Redirige a home.
      const homePath = userRole === "kine" ? "/" : "/paciente-home";
      setState({ kind: "success" });
      navigate(homePath, { replace: true });
      return;
    }

    // userRole === null → huérfano: auth.users sin row de dominio.
    // Es el caso recién-signupeado: leemos metadata + llamamos la RPC.
    const metadata = session.user.user_metadata as Record<string, unknown>;
    const role = metadata.role as string | undefined;

    if (role === "kine") {
      void completeSignupKine();
    } else if (role === "paciente") {
      void completeSignupPaciente();
    } else {
      // Sin metadata role → orphan sin info para resolver. signOut +
      // mensaje. Cleanup admin queda Sprint 3+.
      void (async () => {
        await signOut();
        setState({
          kind: "error",
          title: "No pudimos completar tu cuenta",
          message:
            "Tu cuenta quedó incompleta. Contactanos para destrabarla y volver a registrarte.",
          cta: "contact-support",
        });
      })();
    }

    async function completeSignupKine() {
      const m = session!.user.user_metadata as Record<string, unknown>;
      const { data: rpcData, error: rpcError } = await supabase.rpc(
        "create_kine_row",
        {
          p_nombre: (m.nombre as string) ?? "",
          p_matricula: (m.matricula as string) ?? "",
          p_especialidad: (m.especialidad as string | null) ?? null,
          p_provincia: (m.provincia as string | null) ?? null,
        },
      );

      if (rpcError) {
        console.warn("[AuthCallback] create_kine_row error", rpcError);
        await signOut();
        setState({
          kind: "error",
          title: "No pudimos activar tu cuenta",
          message:
            "Hubo un problema completando tu registro. Probá iniciar sesión en unos minutos.",
          cta: "login",
        });
        return;
      }

      if (rpcData && !rpcData.ok) {
        const code = rpcData.code as string;
        if (code === "ALREADY_KINE") {
          // Race: otra pestaña ya completó. Tratá como éxito.
          setState({ kind: "success" });
          navigate("/", { replace: true });
          return;
        }
        await signOut();
        setState({
          kind: "error",
          title: "No pudimos activar tu cuenta",
          message:
            code === "INVALID_MATRICULA"
              ? "La matrícula que ingresaste no es válida. Volvé a registrarte."
              : code === "EMAIL_TAKEN" || code === "ALREADY_PACIENTE"
                ? "Ya existe una cuenta vinculada a este email. Iniciá sesión."
                : "Hubo un problema completando tu registro. Iniciá sesión o contactanos.",
          cta: "login",
        });
        return;
      }

      // Success — full reload para que AuthProvider re-resuelva userRole.
      // Sin esto, userRole queda null en cache del context y los hooks
      // del dashboard kine no van a disparar queries.
      window.location.assign("/");
    }

    async function completeSignupPaciente() {
      const m = session!.user.user_metadata as Record<string, unknown>;
      const token = m.signup_token as string | undefined;
      const fecha = m.fecha_nacimiento as string | undefined;

      if (!token || !fecha) {
        await signOut();
        setState({
          kind: "error",
          title: "Link incompleto",
          message:
            "El link no trae los datos necesarios. Volvé a abrir la invitación de tu kinesiólogo.",
          cta: "contact-support",
        });
        return;
      }

      const { data: rpcData, error: rpcError } = await supabase.rpc(
        "create_paciente_row",
        {
          p_signup_token: token,
          p_fecha_nacimiento: fecha,
        },
      );

      if (rpcError) {
        console.warn("[AuthCallback] create_paciente_row error", rpcError);
        await signOut();
        setState({
          kind: "error",
          title: "No pudimos activar tu cuenta",
          message:
            "Hubo un problema completando tu registro. Probá iniciar sesión en unos minutos.",
          cta: "login",
        });
        return;
      }

      if (rpcData && !rpcData.ok) {
        const code = rpcData.code as string;
        if (code === "ALREADY_PACIENTE") {
          setState({ kind: "success" });
          navigate("/paciente-home", { replace: true });
          return;
        }
        if (
          code === "TOKEN_INVALID" ||
          code === "TOKEN_EXPIRED" ||
          code === "TOKEN_USED"
        ) {
          await signOut();
          navigate("/signup-paciente/error", {
            state: { code },
            replace: true,
          });
          return;
        }
        await signOut();
        setState({
          kind: "error",
          title: "No pudimos activar tu cuenta",
          message:
            code === "AGE_TOO_LOW"
              ? "Para crear cuenta tenés que tener al menos 16 años."
              : code === "ALREADY_KINE"
                ? "Ya existe una cuenta vinculada a este email. Iniciá sesión."
                : "Hubo un problema completando tu registro. Iniciá sesión o contactanos.",
          cta: "login",
        });
        return;
      }

      // Success — full reload para refrescar userRole en context.
      window.location.assign("/paciente-home");
    }
  }, [status, userId, userRole, session, signOut, navigate]);

  if (state.kind === "working" || state.kind === "success") {
    return (
      <AuthLayout
        title="Activando tu cuenta…"
        subtitle="Un segundo mientras completamos tu registro."
      >
        <div className="flex items-center justify-center py-8">
          {state.kind === "success" ? (
            <CheckCircle2 className="w-12 h-12 text-primary" strokeWidth={1.5} />
          ) : (
            <Loader2 className="w-12 h-12 animate-spin text-primary" />
          )}
        </div>
      </AuthLayout>
    );
  }

  // Error state
  const Icon =
    state.cta === "signup-paciente-error" ? Clock : AlertCircle;
  return (
    <AuthLayout title={state.title}>
      <div className="flex items-center justify-center py-4">
        <Icon className="w-12 h-12 text-red-600" strokeWidth={1.5} />
      </div>
      <p className="text-sm text-muted-foreground text-center mb-6 leading-relaxed">
        {state.message}
      </p>
      {state.cta === "login" && (
        <Link to="/login" className="block">
          <Button type="button" className="w-full">
            Ir al login
          </Button>
        </Link>
      )}
      {state.cta === "contact-support" && (
        <Link to="/login" className="block">
          <Button type="button" variant="outline" className="w-full">
            Volver al login
          </Button>
        </Link>
      )}
    </AuthLayout>
  );
}
